/**
 * Data hooks for entry templates (V5-35).
 *
 * One place that knows the endpoints, so the picker, the manage screen and the
 * form share a cache and cannot drift into three different fetch shapes.
 *
 * THE MUTATIONS ALL INVALIDATE `["templates"]` AND NOTHING ELSE
 * -------------------------------------------------------------
 * Deliberately: no template operation changes an Income, an Expense or a
 * dashboard aggregate, because no template operation WRITES one. `useTemplate`
 * (the "pick" mutation) is the closest thing to a write, and all it touches is
 * the template's own ordering metadata — so the entry-side query keys
 * (`income`, `expenses`, `records`, `dashboard`) are intentionally NOT
 * invalidated here. Whoever actually saves the entry invalidates those, exactly
 * as they already do today.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api/client";
import { apiUrl } from "@/lib/api/config";
import type { EntryTemplate, TemplateInput, TemplatePrefill, TemplateType } from "./types";
import { templateKeys } from "./types";

async function readError(res: Response, fallback: string): Promise<never> {
  const body = (await res.json().catch(() => null)) as { error?: string } | null;
  throw new Error(body?.error || fallback);
}

/**
 * Templates for one entry kind, most recently used first.
 *
 * `includeArchived` is what separates the two surfaces: the PICKER leaves it
 * false and never offers a retired template, while the MANAGE screen passes
 * true so a user can see and restore one. That is the archived-category rule —
 * archiving narrows what is offered going forward, and hides nothing.
 */
export function useTemplateList(
  type: TemplateType | "all",
  options?: { includeArchived?: boolean; enabled?: boolean },
) {
  const includeArchived = options?.includeArchived ?? false;
  return useQuery({
    queryKey: templateKeys.list(type, includeArchived),
    queryFn: async () => {
      const res = await apiFetch(
        apiUrl("templates", "", {
          type: type === "all" ? undefined : type,
          includeArchived: includeArchived ? "true" : undefined,
        }),
      );
      if (!res.ok) return readError(res, "Failed to load templates");
      return (await res.json()) as EntryTemplate[];
    },
    enabled: options?.enabled ?? true,
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: TemplateInput) => {
      const res = await apiFetch(apiUrl("templates"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) return readError(res, "Failed to create template");
      return (await res.json()) as EntryTemplate;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: templateKeys.all }),
  });
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: Partial<TemplateInput> }) => {
      const res = await apiFetch(apiUrl("templates", `/${id}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) return readError(res, "Failed to update template");
      return (await res.json()) as EntryTemplate;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: templateKeys.all }),
  });
}

/**
 * `DELETE` ARCHIVES. It does not delete.
 *
 * The endpoint returns the archived template rather than a 204, which is what
 * lets the caller offer an immediate Undo without a refetch.
 */
export function useArchiveTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(apiUrl("templates", `/${id}`), { method: "DELETE" });
      if (!res.ok) return readError(res, "Failed to archive template");
      return (await res.json()) as EntryTemplate;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: templateKeys.all }),
  });
}

export function useRestoreTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(apiUrl("templates", `/${id}/restore`), { method: "POST" });
      if (!res.ok) return readError(res, "Failed to restore template");
      return (await res.json()) as EntryTemplate;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: templateKeys.all }),
  });
}

/**
 * Permanently remove an ALREADY-ARCHIVED template.
 *
 * Safe in a way purging a Category would not be: nothing references a template.
 * It was COPIED into an entry at save time, not pointed at, so no Income or
 * Expense row changes and no history is rewritten. The server still refuses to
 * purge a template that has not been archived first.
 */
export function usePurgeTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(apiUrl("templates", `/${id}/purge`), { method: "DELETE" });
      if (!res.ok) return readError(res, "Failed to delete template");
      return id;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: templateKeys.all }),
  });
}

/**
 * Mark a template used and get back the entry-form prefill.
 *
 * THIS CREATES NO RECORD. It writes `lastUsedAt` / `useCount` — which is what
 * the "recently used first" ordering reads — and returns the values the form
 * should show. The user reviews them and saves through the ordinary income or
 * expense endpoint, which remains the only thing that has ever written an entry.
 *
 * The list is invalidated on success so the picker re-orders immediately.
 */
export function useUseTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(apiUrl("templates", `/${id}/use`), { method: "POST" });
      if (!res.ok) return readError(res, "Failed to apply template");
      return (await res.json()) as TemplatePrefill;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: templateKeys.all }),
  });
}
